import React, { useState } from 'react';
import { toCurrency } from '../utils/helper';
import dayjs from 'dayjs';
import { Eye } from 'lucide-react';
import Modal from './ui/Modal';

// Helper function to access nested object properties
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current ? current[key] : null;
  }, obj);
};

const ExpandableTable = ({ 
  headers, 
  items = {}, 
  loading = false, 
  refetch, 
  expandableContent,
  expandableKey = 'id'
}) => {
  const itemsArray = Array.isArray(items) ? items : Object.values(items);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const openModal = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  return (
    <>
      <div className='relative overflow-x-auto rounded-lg border border-slate-200 max-h-[500px]'>
        <table className='w-full text-sm text-left'>
          <thead className='text-xs text-gray-700 uppercase bg-gray-50 border-b sticky top-0 z-10'>
            <tr>
              {/* Mobile-optimized view details column */}
              <th className='px-2 sm:px-3 py-3 w-10 sm:w-8 text-center'>
                <span className="text-xs text-gray-500">View</span>
              </th>
            {headers.map((header) => (
              <th
                key={header.key}
                scope='col'
                className={`px-2 sm:px-3 py-3 text-xs sm:text-xs font-semibold first:sticky first:left-0 first:bg-gray-50 first:z-20 ${
                  header.print === false ? 'print:hidden' : ''
                }`}
                style={{ minWidth: header.width || '120px' }}
              >
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!loading ? (
            itemsArray.length > 0 ? (
              itemsArray.map((item, index) => {
                const itemKey = getNestedValue(item, expandableKey) || index;
                
                return (
                  <tr key={itemKey} className='bg-white border-b last:border-0 hover:bg-gray-50 active:bg-gray-100 transition-colors'>
                    {/* Mobile-friendly view details button */}
                    <td className='px-2 sm:px-3 py-3 sm:py-2 w-10 sm:w-8'>
                      <button
                        onClick={() => openModal(item)}
                        className='flex items-center justify-center w-8 h-8 sm:w-6 sm:h-6 rounded-full hover:bg-blue-100 active:bg-blue-200 transition-colors touch-manipulation'
                        aria-label='View details'
                      >
                        <Eye className='w-4 h-4 sm:w-4 sm:h-4 text-blue-600' />
                      </button>
                    </td>
                    
                    {headers.map((header) => (
                      <td
                        key={header.key}
                        className={`px-2 sm:px-3 py-3 sm:py-2 align-top text-sm sm:text-sm first:sticky first:left-0 first:bg-white first:z-20 ${
                          header.print === false ? 'print:hidden' : ''
                        }`}
                      >
                        {header.type === 'currency' ? (
                          <div className="font-medium">
                            ₱{toCurrency(getNestedValue(item, header.key) || 0)}
                          </div>
                        ) : header.type === 'number' ? (
                          <div className="font-medium">
                            {getNestedValue(item, header.key)?.toFixed(2)}
                          </div>
                        ) : header.type === 'date' ? (
                          <div className="text-gray-700">
                            {dayjs(getNestedValue(item, header.key)).format('MMM DD, YYYY')}
                          </div>
                        ) : header.type === 'time' ? (
                          <div className="text-gray-700">
                            {dayjs(getNestedValue(item, header.key), 'HH:mm').format('h:mm A')}
                          </div>
                        ) : header.type === 'status' ? (
                          <span
                            className={`px-2 py-1 font-semibold leading-tight rounded-full text-xs ${
                              getNestedValue(item, header.key)?.class || ''
                            }`}
                          >
                            {getNestedValue(item, header.key)?.text}
                          </span>
                        ) : header.type === 'custom' ? (
                          header.customCell?.({ item, refetch })
                        ) : (
                          <div className="text-gray-900">
                            {getNestedValue(item, header.key)}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr className='bg-white '>
                <td className='px-2 sm:px-3 py-4'></td>
                <td colSpan={headers.length} className='px-2 sm:px-3 py-8 text-center text-gray-500'>
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-gray-400 text-2xl">📊</div>
                    <div>No data available</div>
                  </div>
                </td>
              </tr>
            )
          ) : (
            <tr className='bg-white'>
              <td className='px-2 sm:px-3 py-4'></td>
              <td colSpan={headers.length} className='px-2 sm:px-3 py-8 text-center text-gray-500'>
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <div>Loading...</div>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      
      {/* Modal for expandable content */}
      <Modal 
        isOpen={modalOpen} 
        onClose={closeModal}
        title={selectedItem ? `${selectedItem.name} - Draw Details` : 'Draw Details'}
      >
        {selectedItem && expandableContent && (
          expandableContent({ item: selectedItem, refetch })
        )}
      </Modal>
    </>
  );
};

export default ExpandableTable; 